import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import AppointmentNotesModal from '../components/AppointmentNotesModal';
import { toast } from 'react-toastify';

function BookAppointment() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callType, setCallType] = useState('VIDEO');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [patientNotes, setPatientNotes] = useState('');
  const [medicalReportFile, setMedicalReportFile] = useState(null);

  useEffect(() => {
    fetchDoctor();
  }, [doctorId]);

  useEffect(() => {
    if (doctorId) {
      fetchSlots(callType);
    }
  }, [doctorId, callType]);

  const fetchDoctor = async () => {
    try {
      const docRes = await api.get(`doctor/profile/${doctorId}/`);
      setDoctor(docRes.data);
    } catch (err) {
      console.error("Error fetching doctor:", err);
      if (err.response?.status === 401) navigate('/user/login');
    }
  };

  const fetchSlots = async (type) => {
    setLoading(true);
    setSelectedSlot(null);
    setSelectedDate(null);
    try {
      const slotsRes = await api.get(`appointments/user/slots/${doctorId}/`, {
        params: { call_type: type },
      });
      setSlots(slotsRes.data);

      if (slotsRes.data && slotsRes.data.length > 0) {
        const todayStr = new Date().toISOString().split('T')[0];
        const availableDates = [...new Set(slotsRes.data.map(s => s.date))].sort();

        if (availableDates.includes(todayStr)) {
          setSelectedDate(todayStr);
        } else {
          setSelectedDate(availableDates[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching slots:", err);
      if (err.response?.status === 401) navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  const consultationFee = () => {
    if (!doctor) return null;
    const fee = callType === 'AUDIO'
      ? doctor.audio_consultation_fees
      : doctor.consultation_fees;
    if (fee === null || fee === undefined || fee === '') return null;
    return parseFloat(fee);
  };

  const handleBook = () => {
    if (!selectedSlot) {
      toast.warning("Please select a time slot first.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedSlot.id) {
      toast.error("Invalid slot selected. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    setShowNotesModal(true);
  };

  const handleProceedToPayment = ({ patientNotes, medicalReportFiles, medicalReportFile }) => {
    setShowNotesModal(false);
    const files = Array.isArray(medicalReportFiles) && medicalReportFiles.length
      ? medicalReportFiles
      : (medicalReportFile ? [medicalReportFile] : []);
    const summaryUrl = `/user/booking-summary?slot_id=${selectedSlot.id}`;
    navigate(summaryUrl, {
      state: {
        patientNotes,
        medicalReportFiles: files,
        medicalReportFile: files[0] || null,
        backTo: `/user/book-appointment/${doctorId}`,
      }
    });
  };

  const getImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/300x300?text=Doctor";

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };

  const upcomingSlots = slots.filter(slot => {
    if (!slot.date || !slot.start_time) return false;
    const slotDateTime = new Date(`${slot.date} ${slot.start_time}`);
    const now = new Date();
    return slotDateTime >= now;
  });

  const fee = consultationFee();

  if (loading && !doctor) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8fafc' }}>
      <UserNavbar />

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-0">
                <div className="row g-0 align-items-stretch">
                  <div
                    className="col-md-4 text-white p-4 p-lg-4 d-flex flex-column"
                    style={{ background: 'linear-gradient(165deg, #2563eb 0%, #1d4ed8 55%, #1e40af 100%)' }}
                  >
                    <div className="text-center text-md-start mb-4">
                      <img
                        src={getImageUrl(doctor?.image)}
                        alt={doctor?.name}
                        className="rounded-circle border border-3 border-white mb-3 shadow"
                        style={{ width: 96, height: 96, objectFit: 'cover' }}
                      />
                      <h4 className="fw-bold mb-1">Dr. {doctor?.name}</h4>
                      <p className="mb-0 opacity-75">{doctor?.specialization || 'Specialist'}</p>
                      {(doctor?.average_rating != null || doctor?.total_reviews) && (
                        <p className="small mt-2 mb-0 opacity-90">
                          <i className="bi bi-star-fill text-warning me-1"></i>
                          {doctor?.average_rating != null ? parseFloat(doctor.average_rating).toFixed(1) : '0.0'}
                          {doctor?.total_reviews ? ` (${doctor.total_reviews} reviews)` : ''}
                        </p>
                      )}
                    </div>

                    <div className="mb-4">
                      <div className="rounded-3 p-3 mb-2" style={{ background: 'rgba(255,255,255,0.12)' }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-camera-video-fill"></i>
                          <span className="small fw-semibold">Video consultation</span>
                        </div>
                        <p className="fw-bold mb-0 fs-5">
                          ${doctor?.consultation_fees != null ? parseFloat(doctor.consultation_fees).toFixed(2) : '—'}
                        </p>
                      </div>
                      <div className="rounded-3 p-3" style={{ background: 'rgba(255,255,255,0.12)' }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-telephone-fill"></i>
                          <span className="small fw-semibold">Audio consultation</span>
                        </div>
                        <p className="fw-bold mb-0 fs-5">
                          ${doctor?.audio_consultation_fees != null ? parseFloat(doctor.audio_consultation_fees).toFixed(2) : '—'}
                        </p>
                      </div>
                    </div>

                    <ul className="list-unstyled small mb-4 opacity-90">
                      {doctor?.qualification && (
                        <li className="mb-2 d-flex gap-2">
                          <i className="bi bi-award mt-1"></i>
                          <span>{doctor.qualification}</span>
                        </li>
                      )}
                      {doctor?.experience != null && doctor.experience !== '' && (
                        <li className="mb-2 d-flex gap-2">
                          <i className="bi bi-briefcase mt-1"></i>
                          <span>{doctor.experience} years experience</span>
                        </li>
                      )}
                      {doctor?.address && (
                        <li className="d-flex gap-2">
                          <i className="bi bi-geo-alt mt-1"></i>
                          <span>{doctor.address}</span>
                        </li>
                      )}
                    </ul>

                    <div className="mt-auto rounded-3 p-3" style={{ background: 'rgba(255,255,255,0.15)' }}>
                      <p className="small text-uppercase fw-bold mb-2 opacity-75" style={{ letterSpacing: '0.04em' }}>
                        Selected type
                      </p>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.2)' }}
                        >
                          <i className={`bi ${callType === 'AUDIO' ? 'bi-telephone-fill' : 'bi-camera-video-fill'}`}></i>
                        </div>
                        <div>
                          <p className="fw-bold mb-0">{callType === 'AUDIO' ? 'Audio Call' : 'Video Call'}</p>
                          <p className="small mb-0 opacity-75">
                            {fee != null ? `$${fee.toFixed(2)} + $5 processing` : 'Fee not set'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-8 p-4 p-lg-5">
                    <h5 className="fw-bold mb-3">Book Appointment</h5>

                    <div className="mb-4">
                      <p className="text-muted small text-uppercase fw-bold mb-2">Consultation Type</p>
                      <div className="d-flex gap-2">
                        {['VIDEO', 'AUDIO'].map((type) => (
                          <button
                            key={type}
                            type="button"
                            className={`btn flex-fill rounded-pill py-2 fw-semibold ${callType === type ? 'btn-primary text-white' : 'btn-outline-primary'}`}
                            onClick={() => setCallType(type)}
                          >
                            <i className={`bi ${type === 'VIDEO' ? 'bi-camera-video' : 'bi-telephone'} me-2`}></i>
                            {type === 'VIDEO' ? 'Video Call' : 'Audio Call'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                      </div>
                    ) : upcomingSlots.length === 0 ? (
                      <div className="text-center py-5 bg-light rounded-4">
                        <i className={`bi ${callType === 'AUDIO' ? 'bi-telephone-x' : 'bi-camera-video-off'} fs-1 text-muted mb-3 d-block`}></i>
                        <p className="text-muted fw-medium">No available {callType === 'AUDIO' ? 'audio' : 'video'} slots at the moment.</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 pb-2">
                          <div className="d-flex overflow-auto pb-3 gap-3 date-filter-scroll" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {[...new Set(upcomingSlots.map(s => s.date))].sort().map(date => {
                              const dateObj = new Date(date);
                              const isToday = date === new Date().toISOString().split('T')[0];
                              const isSelected = selectedDate === date;
                              return (
                                <button
                                  key={date}
                                  type="button"
                                  onClick={() => {
                                    setSelectedDate(date);
                                    setSelectedSlot(null);
                                  }}
                                  className={`btn border-0 rounded-4 p-3 d-flex flex-column align-items-center transition-all ${isSelected
                                    ? 'bg-primary text-white shadow'
                                    : 'bg-white text-dark shadow-sm'
                                    }`}
                                  style={{ minWidth: '94px', flexShrink: 0 }}
                                >
                                  <span className={`small fw-bold mb-1 ${isSelected ? 'text-white-50' : 'text-primary'}`}>
                                    {isToday ? 'TODAY' : dateObj.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}
                                  </span>
                                  <span className="fs-4 fw-bold mb-0">
                                    {dateObj.getDate()}
                                  </span>
                                  <span className="small fw-medium">
                                    {dateObj.toLocaleDateString(undefined, { month: 'short' })}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="bg-light p-4 rounded-4 mb-4">
                          <h6 className="fw-bold mb-3 d-flex align-items-center text-dark small text-uppercase">
                            <i className="bi bi-clock me-2 text-primary"></i>
                            {selectedDate ? `Available on ${new Date(selectedDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}` : 'Select a date'}
                          </h6>
                          <div
                            className="pe-2 custom-slots-scroll"
                            style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                              gap: '10px'
                            }}
                          >
                            {upcomingSlots
                              .filter(s => s.date === selectedDate)
                              .map(slot => (
                                <button
                                  key={slot.id}
                                  type="button"
                                  className={`btn rounded-pill py-2 px-3 fw-bold transition-all text-center ${selectedSlot?.id === slot.id ? 'btn-primary shadow-sm scale-105' : 'btn-outline-primary bg-white'
                                    }`}
                                  onClick={() => setSelectedSlot(slot)}
                                >
                                  {slot.start_time.slice(0, 5)} - {slot.end_time.slice(0, 5)}
                                </button>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mt-4 pt-4 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <p className="text-muted small mb-0 text-uppercase fw-bold">Selected Slot</p>
                          <h6 className="fw-bold mb-0">
                            {selectedSlot
                              ? `${selectedSlot.date} at ${selectedSlot.start_time} (${callType === 'AUDIO' ? 'Audio' : 'Video'})`
                              : 'None selected'}
                          </h6>
                        </div>
                        <div className="text-end">
                          <p className="text-muted small mb-0 text-uppercase fw-bold">
                            {callType === 'AUDIO' ? 'Audio' : 'Video'} Fee
                          </p>
                          <h6 className="fw-bold mb-0 text-success">
                            {fee != null ? `$${fee.toFixed(2)} + $5.00 processing` : 'N/A'}
                          </h6>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary btn-lg w-100 rounded-3 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                        disabled={!selectedSlot}
                        onClick={handleBook}
                      >
                        <i className="bi bi-credit-card me-2"></i>
                        Proceed to Payment
                      </button>
                      <p className="small text-muted text-center mt-3">
                        <i className="bi bi-shield-check me-1"></i>
                        Secure appointment booking with FITNOVA Health.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AppointmentNotesModal
        show={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        onProceed={handleProceedToPayment}
      />

      <style>{`
        .transition-all { transition: all 0.3s ease; }
        .scale-105 { transform: scale(1.05); }
        .date-filter-scroll::-webkit-scrollbar { display: none; }
        .date-filter-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .custom-slots-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-slots-scroll::-webkit-scrollbar-track {
          background: #e9ecef;
          border-radius: 10px;
        }
        .custom-slots-scroll::-webkit-scrollbar-thumb {
          background: #0d6efd;
          border-radius: 10px;
        }
        .custom-slots-scroll::-webkit-scrollbar-thumb:hover {
          background: #0b5ed7;
        }
      `}</style>
    </div>
  );
}

export default BookAppointment;
